import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { LIST_ACTION_CONCURRENCY } from "containers/Resources/listActionConcurrency";
import { GLOBAL_LIST_ACTION_SCOPE } from "containers/Resources/listActionScope";
import { all, call, put } from "redux-saga/effects";

// Reference-count concurrent bulk actions PER SCOPE so a sibling action still
// running in the same table isn't cleared out from under it, while independent
// tables clear independently.
const activeListActionsByScope = new Map();

function* updateListActionProgress(current, total, label, scopeId) {
    yield put({
        type: resourcesActions.LIST_ACTION_PROGRESS_UPDATE,
        progress: { current, total, label, scopeId },
    });
}

function* clearListActionProgress(scopeId) {
    const key = scopeId || GLOBAL_LIST_ACTION_SCOPE;
    const next = Math.max(0, (activeListActionsByScope.get(key) || 0) - 1);
    if (next === 0) {
        activeListActionsByScope.delete(key);
        yield put({ type: resourcesActions.LIST_ACTION_PROGRESS_CLEAR, scopeId });
    } else {
        activeListActionsByScope.set(key, next);
    }
}

/**
 * Process resources in batches of LIST_ACTION_CONCURRENCY parallel saga workers.
 * @param {Array} resources
 * @param {string} label Progress label shown in the toolbar
 * @param {function*} processOne Generator (resource) => result
 * @param {string} [scopeId] Acting table's scope (its tableSettingsKey); omitted
 *   => global/unscoped (shows in every toolbar).
 */
export function* runBatchedListAction(resources, label, processOne, scopeId) {
    const total = resources.length;
    const results = [];

    const key = scopeId || GLOBAL_LIST_ACTION_SCOPE;
    activeListActionsByScope.set(key, (activeListActionsByScope.get(key) || 0) + 1);

    try {
        if (total > 0) {
            yield* updateListActionProgress(0, total, label, scopeId);
        }

        for (let start = 0; start < total; start += LIST_ACTION_CONCURRENCY) {
            const batch = resources.slice(start, start + LIST_ACTION_CONCURRENCY);
            const batchResults = yield all(batch.map((res) => call(processOne, res)));
            results.push(...batchResults);
            yield* updateListActionProgress(
                Math.min(start + batch.length, total),
                total,
                label,
                scopeId
            );
        }

        return results;
    } finally {
        yield* clearListActionProgress(scopeId);
    }
}
